import { useEffect, useState } from 'react'

/**
 * Loads an image cross-origin, samples the central region, and returns the
 * average perceived luminance (0–1). Returns null while loading or if the
 * image taints the canvas (CORS).
 */
export function useImageLuminance(url: string | null | undefined): number | null {
  const [luminance, setLuminance] = useState<number | null>(null)

  useEffect(() => {
    if (!url) {
      setLuminance(null)
      return
    }

    let cancelled = false
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = url

    img.onload = () => {
      if (cancelled) return
      try {
        const sample = 32
        const canvas = document.createElement('canvas')
        canvas.width = sample
        canvas.height = sample
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) return

        // Draw the central square of the image scaled into the sample canvas.
        const side = Math.min(img.naturalWidth, img.naturalHeight)
        const sx = (img.naturalWidth - side) / 2
        const sy = (img.naturalHeight - side) / 2
        ctx.drawImage(img, sx, sy, side, side, 0, 0, sample, sample)

        const { data } = ctx.getImageData(0, 0, sample, sample)
        let total = 0
        const pixels = data.length / 4
        for (let i = 0; i < data.length; i += 4) {
          // Rec. 709 luma
          total += 0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!
        }
        if (!cancelled) setLuminance(total / pixels / 255)
      } catch {
        if (!cancelled) setLuminance(null)
      }
    }

    return () => {
      cancelled = true
      img.onload = null
    }
  }, [url])

  return luminance
}
